<template>
    <div>
        <v-menu
            v-model="menu_state"
            :close-on-content-click="false"
            offset-y
            eager
            :nudge-bottom="5"
            min-width="288px"
            max-width="360px"
            :content-class="
                isMobile
                    ? $route.query.ukuwinversion
                        ? ' miniuku-notifications-menu mobile-notifications-menu-position'
                        : ' notifications-menu mobile-notifications-menu-position'
                    : 'notifications-menu notifications-menu-position'
            "
        >
            <template v-slot:activator="{ on }">
                <v-btn
                    rounded
                    class="round-btn glass "
                    v-on="on"
                >
                    <icon icon="Bell" color="main" />
                    <transition name="bounce-transition" appear>
                        <span
                            v-if="showBadge"
                            :key="showBadge"
                            class="notification-badge"
                        >
                            <span v-if="showBadge < 10">
                                {{ showBadge }}
                            </span>
                            <span v-else>9+</span>
                        </span>
                    </transition>
                </v-btn>
            </template>
            <v-card
                light
                class="elevation-0"
            >
                <v-row
                    no-gutters
                    align="baseline"
                >
                    <v-col class="subtitle-2 font-weight-bold py-2 px-5">
                        {{ $t("Notifications") }}
                    </v-col>
                </v-row>
                <ScrollShadow
                    style="max-height: 350px"
                    ref="notificationListElem"
                >
                    <v-list
                        style="min-width: 288px;"
                        class="py-0 px-1"
                        dense
                    >
                        <NotificationListItem
                            v-for="notification in notifications"
                            :key="notification.id"
                            :notification="notification"
                            style="border-bottom: 1px lightgrey"
                        />
                        <v-list-item
                            v-if="loader.isLoading"
                            class="d-flex align-center justify-center"
                        >
                            <LoaderDefault
                                :size="24"
                                :width="2"
                            />
                        </v-list-item>
                    </v-list>
                </ScrollShadow>
                <v-divider />
                <v-row no-gutters>
                    <v-col class="d-flex justify-end align-center">
                        <v-btn
                            small
                            text
                            class="overline primary--text"
                            @click="onSeeAllClick"
                        >
                            {{ $t("View all") }}
                        </v-btn>
                        <v-btn
                            v-if="notifications.length > 0"
                            small
                            text
                            class="overline primary--text"
                            @click="markAllNotificationsSeen"
                        >
                            {{ $t("Mark all as read") }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card>
        </v-menu>
        <v-snackbar
            v-if="$vuetify.breakpoint.mdAndUp"
            :value="!!latestStickyNotifications"
            :timeout="0"
            color="transparent"
            absolute
            top
            right
            style="top: 60px;"
            class="notification-snackbar"
            @mouseover="isStickyHovered = true"
            @mouseleave="isStickyHovered = false"
        >
            <v-fade-transition>
                <NotificationListItem
                    v-if="latestStickyNotifications[0]"
                    :notification="latestStickyNotifications[0]"
                    is-popup
                    :notification-count="latestStickyNotifications.length"
                    :show-master-popup="!isStickyHovered"
                    is-master-popup
                    @hide-all="onHideAll"
                />
            </v-fade-transition>
            <v-slide-y-transition>
                <div v-show="isStickyHovered">
                    <NotificationListItem
                        v-for="notification in latestStickyNotifications.slice(1, latestStickyNotifications.length)"
                        :key="notification.id"
                        :notification="notification"
                        is-popup
                    />
                </div>
            </v-slide-y-transition>
        </v-snackbar>
        <v-snackbar
            v-if="$vuetify.breakpoint.mdAndUp"
            :value="true"
            :timeout="0"
            color="transparent"
            absolute
            top
            right
            style="top: 60px;"
            :transition="false"
            class="latest-notification-snackbar notification-snackbar"
        >
            <NotificationListItem
                v-for="notification in latestNotifications"
                :key="notification.id"
                :notification="notification"
                is-popup
                is-latest-notification
            />
        </v-snackbar>
    </div>
</template>

<script>
import NotificationListItem from "./NotificationListItem";
import NotificationModule from "../../store/modules/notification";
import {ref, watch, computed, onMounted, reactive, onUnmounted} from "@vue/composition-api";
import router from "../../router";
import ScrollShadow from "@/components/ScrollShadow.vue";
import Loader from "@/utility/loader";
import LoaderDefault from "@/components/LoaderDefault.vue";

export default {
    components: {
        ScrollShadow,
        NotificationListItem,
        LoaderDefault
    },
    props: {
        isMobile: {
            type: Boolean
        }
    },
    setup(props, ctx) {
        const {
            notifications,
            markAllNotificationsSeen,
            markNotificationsAlerted,
            fetchNotifications,
            latestNotifications,
            hideNotifications,
            latestStickyNotifications
        } = NotificationModule();
        const loader = reactive(new Loader());
        const menu_state = ref(false);
        const isStickyHovered = ref(false);
        const notificationListElem = ref(null)
        const hasAllNotificationsLoaded = ref(false)

        const defaultNotificationLoadCount = 50

        const notificationLoadCount = ref(0)

        watch(menu_state, async val => {
            if (val) {
                if (ctx.root._route.name !== 'notifications') {
                    await loadNotifications()
                }

                await markNotificationsAlerted()
            } else {
                setTimeout(() => {
                    notificationListElem.value.$el.scrollTop = 0
                }, 200)

                notificationLoadCount.value = 0
                hasAllNotificationsLoaded.value = false
            }
        });

        const loadNotifications = () => {
            if (loader.isLoading || hasAllNotificationsLoaded.value) {
                return
            }

            loader.withLoader(async () => {
                notificationLoadCount.value += defaultNotificationLoadCount
                const data = await fetchNotifications(notificationLoadCount.value)

                if (data.notifications.length < notificationLoadCount.value) {
                    hasAllNotificationsLoaded.value = true
                }
            })
        }

        const onSeeAllClick = () => {
            router.push({ name: "notifications" });
            menu_state.value = false;
        };

        const showBadge = computed(() => {
            const latestNotificationIds = latestNotifications.value.map(x => x.id)

            return notifications.value.filter(notification => !latestNotificationIds.includes(notification.id) && notification.is_alerted === 0).length
        })

        const onHideAll = () => {
            hideNotifications()

            setTimeout(() => {
                // mouseover is still called afterwards, so currently used setTimeout
                isStickyHovered.value = false
            }, 1000)
        }

        const scrollListener = () => {
            if (ctx.root._route.name === 'notifications') {
                return
            }

            if(notificationListElem.value.$el.scrollTop + notificationListElem.value.$el.clientHeight >= notificationListElem.value.$el.scrollHeight) {
                loadNotifications()
            }
        }

        onMounted(() => {
            notificationListElem.value.$el.addEventListener('scroll', scrollListener);
        })

        onUnmounted(() => {
            notificationListElem.value.$el.removeEventListener('scroll', scrollListener)
        })
        
        return {
            menu_state,
            notifications,
            onSeeAllClick,
            markAllNotificationsSeen,
            showBadge,
            isStickyHovered,
            latestNotifications,
            onHideAll,
            notificationListElem,
            loader,
            latestStickyNotifications
        };
    }
};
</script>

<style scoped lang="scss">
.notification-snackbar {
    ::v-deep .v-snack__wrapper {
        box-shadow: none !important;
    }

    ::v-deep .v-snack__content {
        overflow: visible;
        display: flex;
        flex-direction: column;
    }
}
</style>